import type React from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Needed so provider props remain inferable per entry.
export type StoreProviderMap = Record<string, React.ComponentType<any>>;

type ExtractStateFromProvider<P> =
  P extends React.ComponentType<infer Props> ? (Props extends { initialState?: Partial<infer S> } ? S : never) : never;

type ExtractStoreInstanceFromProvider<P> =
  P extends React.ComponentType<infer Props> ? (Props extends { store?: infer StoreType } ? StoreType : never) : never;

export type TestStoreProviderProps<TStoreProviders extends StoreProviderMap> = {
  [K in keyof TStoreProviders & string as `initial${K}Data`]?: Partial<ExtractStateFromProvider<TStoreProviders[K]>>;
} & {
  [K in keyof TStoreProviders & string as `${Uncapitalize<K>}Store`]?: ExtractStoreInstanceFromProvider<
    TStoreProviders[K]
  >;
} & {
  children: React.ReactNode;
};

type ProviderEntry<TStoreProviders extends StoreProviderMap> = {
  [K in keyof TStoreProviders & string]: {
    Provider: TStoreProviders[K];
    providerProps: {
      initialState?: Partial<ExtractStateFromProvider<TStoreProviders[K]>>;
      store?: ExtractStoreInstanceFromProvider<TStoreProviders[K]>;
    };
  };
}[keyof TStoreProviders & string];

type ProviderPropType = 'initialData' | 'store';

function extractStoreNameFromPropKey(propKey: string): { storeName: string | null; propType: ProviderPropType } {
  const initialPrefix = 'initial';
  const dataSuffix = 'Data';
  if (propKey.startsWith(initialPrefix) && propKey.endsWith(dataSuffix)) {
    const storeName = propKey.slice(initialPrefix.length, -dataSuffix.length);
    return { storeName, propType: 'initialData' };
  }

  const storeSuffix = 'Store';
  if (propKey.endsWith(storeSuffix)) {
    const withoutSuffix = propKey.slice(0, -storeSuffix.length);
    const storeName = withoutSuffix.charAt(0).toUpperCase() + withoutSuffix.slice(1);
    return { storeName, propType: 'store' };
  }

  return { storeName: null, propType: 'initialData' };
}

/**
 * Creates a test-only provider that mounts only the store providers requested by its props.
 *
 * The returned component supports `initial{StoreName}Data` for provider-created stores and
 * `{storeName}Store` for externally created store instances.
 */
export function createTestStoreProvider<TStoreProviders extends StoreProviderMap>(
  storeProviders: TStoreProviders,
): (props: TestStoreProviderProps<TStoreProviders>) => React.ReactNode {
  const TestStoreProvider = ({ children, ...props }: TestStoreProviderProps<TStoreProviders>) => {
    const providersToMount: ProviderEntry<TStoreProviders>[] = [];
    const storesBeingMounted = new Map<string, ProviderPropType>();

    Object.entries(props).forEach(([key, value]) => {
      if (value === undefined) {
        return;
      }

      const { storeName, propType } = extractStoreNameFromPropKey(key);

      if (!storeName) {
        throw new Error(
          `Invalid prop key "${key}". Test store props must follow "initial{StoreName}Data" or "{storeName}Store".`,
        );
      }

      const Provider = storeProviders[storeName as keyof TStoreProviders & string];

      if (!Provider) {
        throw new Error(
          `Unknown store "${storeName}". Available stores: ${Object.keys(storeProviders).join(', ')}. ` +
            `Did you forget to add it to the createTestStoreProvider() map?`,
        );
      }

      const existingPropType = storesBeingMounted.get(storeName);
      if (existingPropType) {
        const storePropName = storeName.charAt(0).toLowerCase() + storeName.slice(1);
        throw new Error(
          `Cannot provide both "initial${storeName}Data" and "${storePropName}Store" for the same store. Use one or the other.`,
        );
      }
      storesBeingMounted.set(storeName, propType);

      const providerProps: Record<string, unknown> = {};
      if (propType === 'initialData') {
        providerProps.initialState = value;
      } else {
        providerProps.store = value;
      }

      providersToMount.push({
        Provider,
        providerProps,
      } as ProviderEntry<TStoreProviders>);
    });

    return providersToMount.reduceRight((content, { Provider, providerProps }) => {
      const TypedProvider = Provider as React.ComponentType<Record<string, unknown> & { children: React.ReactNode }>;
      return <TypedProvider {...providerProps}>{content}</TypedProvider>;
    }, children);
  };

  TestStoreProvider.displayName = 'TestStoreProvider';

  return TestStoreProvider;
}
